"use client";

import { PortableText } from "@portabletext/react";
import { PortableTextBlock } from "sanity";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { FadeIn } from "@/components/global/utils/FadeIn";
import { IStatItem } from "@/sanity/pages/PageCourseGalleryTemplate";
import { CountUpValue, PipeChip } from "./_shared";

interface StatsSectionProps {
  chip?: string;
  title?: PortableTextBlock[];
  items?: IStatItem[];
}

// "Big Number" band — Figma 3780:19232. Four equal cards, the numeral pinned to
// the top and the label/description pushed to the bottom (justify-between).
export default function StatsSection({ chip, title, items }: StatsSectionProps) {
  const stats = items ?? [];

  return (
    <section className="flex w-full justify-center bg-black px-[2rem] pt-[7rem] pb-[10rem] lg:px-[45rem] lg:pb-[17.5rem]">
      <div className="flex w-[min(150rem,100%)] flex-col items-center gap-[5rem] lg:gap-[6.5rem]">
        {/* Heading */}
        <div className="flex flex-col items-center gap-[2.5rem] text-center lg:gap-[4rem]">
          {chip && (
            <FadeIn delay={0}>
              <PipeChip>{chip}</PipeChip>
            </FadeIn>
          )}
          {title && (
            <FadeIn
              delay={0.1}
              className="richtext richtext-center max-w-full [text-wrap:balance] lg:w-[79.75rem] prose-p:m-0 prose-p:font-news prose-p:text-[3.75rem] prose-p:leading-[1.1] prose-p:tracking-[-0.15rem] prose-p:text-white/90 lg:prose-p:text-[6rem] lg:prose-p:tracking-[-0.25rem] prose-em:font-news prose-em:italic prose-headings:m-0 prose-headings:font-news prose-headings:text-[4.5rem] prose-headings:leading-[1.1] prose-headings:tracking-[-0.15rem] prose-headings:text-white/90 lg:prose-headings:text-[6rem] lg:prose-headings:tracking-[-0.25rem]">
              <PortableText
                value={title}
                components={richTextComponent}
              />
            </FadeIn>
          )}
        </div>

        {/* Cards — 2-up on mobile, 4-up from lg (the design only shows 4-up) */}
        {stats.length > 0 && (
          <div className="grid w-full grid-cols-1 gap-[1.25rem] lg:grid-cols-4">
            {stats.map((stat, i) => (
              <FadeIn
                key={i}
                delay={i * 0.08}
                className="flex h-[22.25rem] flex-col justify-between rounded-[0.5rem] bg-white/[0.08] px-[2rem] pt-[3.5rem] pb-[2rem] lg:h-[41.125rem] lg:px-[3rem] lg:pt-[4.75rem] lg:pb-[3rem]">
                <p className="m-0 font-news text-[6rem] leading-[1.1] tracking-[-0.16rem] text-white lg:text-[8rem]">
                  {stat.value && <CountUpValue value={stat.value} />}
                </p>
                <div className="flex flex-col gap-[0.75rem]">
                  {stat.label && (
                    <p className="m-0 font-geist text-[2.5rem] leading-[1.31] text-white lg:text-[2.75rem]">
                      {stat.label}
                    </p>
                  )}
                  {stat.description && (
                    <p className="m-0 font-geist text-[2rem] leading-[1.5] tracking-[0.045rem] text-white opacity-60 lg:text-[2.25rem]">
                      {stat.description}
                    </p>
                  )}
                </div>
              </FadeIn>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
